import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Avatar,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Devices as DevicesIcon,
  LocationOn as LocationIcon,
  Battery90 as BatteryIcon,
  SignalWifi4Bar as SignalIcon,
  Build as MaintenanceIcon,
  Assignment as AssignIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  SimCard as SimCardIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import DiagnosticoDispositivo from '../../components/devices/DiagnosticoDispositivo';
import PerfilDeUso from '../../components/devices/PerfilDeUso';
import { Device } from '../../types';
import { formatDate, formatTimeAgo, getStatusColor, getStatusLabel, calculateDeviceHealth } from '../../utils/helpers';

const EVENT_CODES: Record<number, { name: string; label: string; category: string; color: 'error' | 'warning' | 'success' | 'info' | 'default' }> = {
  1:  { name: 'sos', label: 'SOS', category: 'alert', color: 'error' },
  2:  { name: 'powerOn', label: 'Encendido', category: 'power', color: 'info' },
  3:  { name: 'ignitionOn', label: 'Ignición ON', category: 'ignition', color: 'success' },
  4:  { name: 'ignitionOff', label: 'Ignición OFF', category: 'ignition', color: 'default' },
  5:  { name: 'doorOpen', label: 'Puerta abierta', category: 'io', color: 'warning' },
  6:  { name: 'doorClose', label: 'Puerta cerrada', category: 'io', color: 'info' },
  17: { name: 'lowPower', label: 'Energía baja', category: 'power', color: 'warning' },
  18: { name: 'powerCut', label: 'Corte energía', category: 'power', color: 'error' },
  19: { name: 'powerRestored', label: 'Energía restaurada', category: 'power', color: 'success' },
  20: { name: 'lowBattery', label: 'Batería baja', category: 'power', color: 'warning' },
  21: { name: 'gpsAntennaCut', label: 'Antena GPS cortada', category: 'gps', color: 'error' },
  22: { name: 'overspeed', label: 'Exceso velocidad', category: 'driving', color: 'warning' },
  27: { name: 'gpsLost', label: 'GPS perdido', category: 'gps', color: 'error' },
  28: { name: 'gpsRestored', label: 'GPS restaurado', category: 'gps', color: 'success' },
  35: { name: 'vibration', label: 'Vibración', category: 'motion', color: 'info' },
  36: { name: 'accOn', label: 'ACC ON', category: 'ignition', color: 'success' },
  37: { name: 'accOff', label: 'ACC OFF', category: 'ignition', color: 'default' },
  38: { name: 'idle', label: 'Ralentí', category: 'driving', color: 'info' },
  39: { name: 'hardAcceleration', label: 'Aceleración brusca', category: 'driving', color: 'warning' },
  40: { name: 'hardBraking', label: 'Frenado brusco', category: 'driving', color: 'warning' },
  41: { name: 'hardCornering', label: 'Giro brusco', category: 'driving', color: 'warning' },
  42: { name: 'accident', label: 'Accidente', category: 'alert', color: 'error' },
  43: { name: 'fatigueDriving', label: 'Fatiga', category: 'driving', color: 'warning' },
  44: { name: 'fatigueRelieve', label: 'Descanso fatiga', category: 'driving', color: 'success' },
  45: { name: 'overtimeDriving', label: 'Conducción excesiva', category: 'driving', color: 'warning' },
  46: { name: 'tempHigh', label: 'Temperatura alta', category: 'environment', color: 'error' },
  47: { name: 'tempLow', label: 'Temperatura baja', category: 'environment', color: 'warning' },
  48: { name: 'fuelSteal', label: 'Robo combustible', category: 'fuel', color: 'error' },
  49: { name: 'fuelLow', label: 'Combustible bajo', category: 'fuel', color: 'warning' },
  50: { name: 'jamming', label: 'Interferencia GPS', category: 'security', color: 'error' },
  51: { name: 'jammingRestored', label: 'Interferencia cesó', category: 'security', color: 'success' },
  52: { name: 'tampering', label: 'Manipulación', category: 'security', color: 'error' },
  55: { name: 'removing', label: 'Remoción', category: 'security', color: 'error' },
  57: { name: 'lowSpeed', label: 'Velocidad baja', category: 'driving', color: 'info' },
};

const CATEGORY_LABELS: Record<string, string> = {
  alert: 'Alertas',
  power: 'Energía',
  ignition: 'Ignición',
  io: 'I/O',
  gps: 'GPS',
  driving: 'Conducción',
  motion: 'Movimiento',
  security: 'Seguridad',
  environment: 'Ambiente',
  fuel: 'Combustible',
  unknown: 'Desconocido',
};

const getEventInfo = (code: number) =>
  EVENT_CODES[code] || { name: `code_${code}`, label: `Código ${code}`, category: 'unknown', color: 'default' as const };

interface DeviceDetailPageProps {}

const DeviceDetailPage: React.FC<DeviceDetailPageProps> = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();

  const [mapOpen, setMapOpen] = useState(false);
  const [traccarInfo, setTraccarInfo] = useState<any>(null);
  const [traccarLoading, setTraccarLoading] = useState(false);
  const [posHistory, setPosHistory] = useState<any[]>([]);
  const [posLoading, setPosLoading] = useState(false);

  // Diagnostics state
  const [diagEvents, setDiagEvents] = useState<any[]>([]);
  const [diagMeta, setDiagMeta] = useState<any>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState<string | null>(null);
  const [diagFrom, setDiagFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 16);
  });
  const [diagTo, setDiagTo] = useState(() => new Date().toISOString().slice(0, 16));
  const [diagPage, setDiagPage] = useState(0);
  const [diagRowsPerPage, setDiagRowsPerPage] = useState(25);
  const [diagExpandedRow, setDiagExpandedRow] = useState<number | null>(null);
  const [diagCategoryFilter, setDiagCategoryFilter] = useState<string | null>(null);

  const loadDiagnostics = useCallback(async () => {
    if (!deviceId) return;
    setDiagLoading(true);
    setDiagError(null);
    setDiagExpandedRow(null);
    setDiagPage(0);
    try {
      const from = new Date(diagFrom).toISOString();
      const to = new Date(diagTo).toISOString();
      const res = await apiService.getDeviceTraccarDiagnostics(deviceId, from, to);
      const data = res?.data || res;
      setDiagEvents(data.events || []);
      setDiagMeta({ totalPositions: data.totalPositions, totalEvents: data.totalEvents, range: data.range });
    } catch (e: any) {
      setDiagError(e.message || 'Error cargando diagnóstico');
    }
    setDiagLoading(false);
  }, [deviceId, diagFrom, diagTo]);

  const diagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const ev of diagEvents) {
      const info = getEventInfo(ev.code);
      counts[info.category] = (counts[info.category] || 0) + 1;
    }
    return counts;
  }, [diagEvents]);

  const filteredDiagEvents = useMemo(() => {
    if (!diagCategoryFilter) return diagEvents;
    return diagEvents.filter((ev: any) => getEventInfo(ev.code).category === diagCategoryFilter);
  }, [diagEvents, diagCategoryFilter]);

  const setDiagPreset = (hours: number) => {
    const to = new Date();
    const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
    setDiagFrom(from.toISOString().slice(0, 16));
    setDiagTo(to.toISOString().slice(0, 16));
  };

  const exportDiagCSV = () => {
    const rows = filteredDiagEvents.map((ev: any) => {
      const info = getEventInfo(ev.code);
      return [
        ev.time, info.category, ev.code, info.label, info.name,
        ev.lat, ev.lon, ((ev.speed || 0) * 1.852).toFixed(1),
        ev.attributes?.battery ?? '', ev.attributes?.power ?? '',
        ev.course ?? '', ev.altitude ?? '', ev.valid ? 'Si' : 'No',
      ].join(',');
    });
    const header = 'Timestamp,Categoría,Código,Nombre,Key,Lat,Lon,Velocidad(km/h),Batería,Power,Curso,Altitud,GPS Válido';
    const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `diagnostico-${deviceId}-${diagFrom.slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const {
    data: deviceData,
    loading,
    error,
    execute: fetchDevice,
  } = useApi(() => deviceId ? apiService.getDevice(deviceId) : Promise.reject('No device ID'));

  useEffect(() => {
    if (deviceId) {
      fetchDevice();
    }
  }, [deviceId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error cargando dispositivo: {error}
        <Button onClick={() => fetchDevice()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  const device = deviceData;

  if (!device) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Dispositivo no encontrado
      </Alert>
    );
  }

  const deviceHealth = calculateDeviceHealth(device);
  const isOnline = device.lastSeen && new Date(device.lastSeen) > new Date(Date.now() - 5 * 60 * 1000);

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return 'success';
      case 'good': return 'success';
      case 'fair': return 'warning';
      case 'poor': return 'error';
      default: return 'default';
    }
  };

  const getHealthLabel = (health: string) => {
    switch (health) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Bueno';
      case 'fair': return 'Regular';
      case 'poor': return 'Malo';
      default: return 'Desconocido';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/devices')}
          >
            Dispositivos
          </Link>
          <Typography variant="body2" color="text.primary">
            {device.name || device.imei}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {device.name || 'Detalles del Dispositivo'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {device.brand} {device.model} - IMEI: {device.imei}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <IconButton
              color="primary"
              onClick={() => fetchDevice()}
              title="Actualizar datos"
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/devices')}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/devices/${deviceId}/edit`)}
            >
              Editar
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Device Info */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            {/* Basic Info Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: getStatusColor(device.status) + '.main',
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    <DevicesIcon fontSize="large" />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {device.name || `${device.brand} ${device.model}`}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      {device.name ? `${device.brand} ${device.model} • ` : ''}IMEI: {device.imei}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={getStatusLabel(device.status)}
                        color={getStatusColor(device.status) as any}
                        size="small"
                      />
                      <Chip
                        label={isOnline ? 'En Línea' : 'Desconectado'}
                        color={isOnline ? 'success' : 'error'}
                        size="small"
                      />
                      <Chip
                        label={`Salud: ${getHealthLabel(deviceHealth)}`}
                        color={getHealthColor(deviceHealth) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Technical Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Información Técnica
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            IMEI
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {device.imei}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Marca
                          </Typography>
                          <Typography variant="body1">{device.brand}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Modelo
                          </Typography>
                          <Typography variant="body1">{device.model}</Typography>
                        </Box>
                        {(device as any).firmwareVersion && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Firmware
                            </Typography>
                            <Typography variant="body1">{(device as any).firmwareVersion}</Typography>
                          </Box>
                        )}
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack spacing={2}>
                        {device.simCardId && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              SIM Card (ICCID)
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: 'monospace',
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={() => {
                                const simId = typeof device.simCardId === 'object' ? device.simCardId._id : device.simCardId;
                                navigate(`/simcards/${simId}`);
                              }}
                            >
                              {typeof device.simCardId === 'object' ? (device.simCardId.iccid || device.simCardId._id) : device.simCardId}
                            </Typography>
                            {typeof device.simCardId === 'object' && (device.simCardId as any).msisdn && (
                              <Typography variant="caption" color="text.secondary">
                                Tel: {(device.simCardId as any).msisdn}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {device.lastSeen && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Última Conexión
                            </Typography>
                            <Typography variant="body1">
                              {formatTimeAgo(device.lastSeen)}
                            </Typography>
                          </Box>
                        )}
                        {(device as any).batteryLevel !== undefined && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Nivel de Batería
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BatteryIcon color={(device as any).batteryLevel > 30 ? 'success' : 'error'} />
                              <Typography variant="body1">{(device as any).batteryLevel}%</Typography>
                            </Box>
                          </Box>
                        )}
                        {(device as any).signalStrength !== undefined && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Señal
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SignalIcon color={(device as any).signalStrength > -70 ? 'success' : 'warning'} />
                              <Typography variant="body1">{(device as any).signalStrength} dBm</Typography>
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

                {/* Location Information */}
                {device.lastPosition && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Ultima Ubicacion
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            <LocationIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="body2">
                                {device.lastPosition.latitude?.toFixed(6)}, {device.lastPosition.longitude?.toFixed(6)}
                              </Typography>
                              {device.lastPosition.address && (
                                <Typography variant="caption" color="text.secondary">
                                  {device.lastPosition.address}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(device.lastPosition.timestamp || device.lastPosition.deviceTime || device.lastPosition.fixTime)}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Stack spacing={0.5}>
                            {device.lastPosition.speed !== undefined && (
                              <Typography variant="body2">
                                Velocidad: <strong>{(device.lastPosition.speed * 1.852).toFixed(0)} km/h</strong>
                              </Typography>
                            )}
                            {device.lastPosition.course !== undefined && (
                              <Typography variant="body2">
                                Rumbo: {device.lastPosition.course?.toFixed(0)}°
                              </Typography>
                            )}
                            {device.lastPosition.altitude !== undefined && (
                              <Typography variant="body2">
                                Altitud: {device.lastPosition.altitude?.toFixed(0)} m
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LocationIcon />}
                        sx={{ mt: 1 }}
                        onClick={() => setMapOpen(true)}
                      >
                        Ver en Mapa
                      </Button>
                    </Box>
                  </>
                )}

                {/* Assignment Information */}
                {(device as any).assignment && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Asignación
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {(device as any).assignment.clientName}
                        </Typography>
                        {(device as any).assignment.vehiclePlate && (
                          <Typography variant="body2" color="text.secondary">
                            Vehículo: {(device as any).assignment.vehiclePlate}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Asignado: {formatDate((device as any).assignment.assignedDate)}
                        </Typography>
                      </Paper>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Service Status - Expiration & SIM */}
            {(() => {
              const expTime = (device as any).expirationTime;
              const simStatus = (device as any).simCardStatus;
              const simDeactivatedAt = (device as any).simCardDeactivatedAt;
              const hasExpiration = !!expTime;
              const expDate = hasExpiration ? new Date(expTime) : null;
              const now = new Date();
              const daysUntil = expDate ? Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
              const isExpired = daysUntil !== null && daysUntil < 0;
              const isExpiringSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 30;
              const isActive = daysUntil !== null && daysUntil > 30;

              const statusColor = isExpired ? 'error' : isExpiringSoon ? 'warning' : isActive ? 'success' : 'default';
              const StatusIcon = isExpired ? ErrorIcon : isExpiringSoon ? WarningIcon : CheckCircleIcon;
              const statusLabel = isExpired
                ? `Expirado hace ${Math.abs(daysUntil!)} días`
                : isExpiringSoon
                ? `Expira en ${daysUntil} días`
                : isActive
                ? `Activo (${daysUntil} días restantes)`
                : 'Sin fecha de expiración';

              return (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon color="primary" />
                      Estado del Servicio
                    </Typography>

                    <Box sx={{
                      p: 2, borderRadius: 2, mb: 2,
                      bgcolor: isExpired ? 'error.50' : isExpiringSoon ? 'warning.50' : isActive ? 'success.50' : 'grey.100',
                      border: '1px solid',
                      borderColor: isExpired ? 'error.200' : isExpiringSoon ? 'warning.200' : isActive ? 'success.200' : 'grey.300',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StatusIcon color={statusColor as any} sx={{ fontSize: 32 }} />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {statusLabel}
                          </Typography>
                          {hasExpiration && (
                            <Typography variant="body2" color="text.secondary">
                              Fecha de expiración: {formatDate(expTime)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Estado SIM</Typography>
                          <Chip
                            icon={<SimCardIcon />}
                            label={simDeactivatedAt ? 'Desactivada' : simStatus === 'active' ? 'Activa' : simStatus || 'N/A'}
                            color={simDeactivatedAt ? 'error' : simStatus === 'active' ? 'success' : 'default'}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </Grid>
                      {simDeactivatedAt && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">SIM desactivada el</Typography>
                            <Typography variant="body1">{formatDate(simDeactivatedAt)}</Typography>
                            {(device as any).simCardDeactivationReason && (
                              <Typography variant="caption" color="text.secondary">
                                Razón: {(device as any).simCardDeactivationReason}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      )}
                      {(device as any).installationDate && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Fecha de Instalación</Typography>
                            <Typography variant="body1">{formatDate((device as any).installationDate)}</Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Revision Info */}
            {device.revisionInfo && device.revisionInfo.isInRevision && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Estado de Revisión
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Este dispositivo está actualmente en revisión
                  </Alert>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Razón
                      </Typography>
                      <Typography variant="body1">{device.revisionInfo.revisionReason}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Prioridad
                      </Typography>
                      <Chip
                        label={device.revisionInfo.priority}
                        size="small"
                        color={device.revisionInfo.priority === 'urgent' ? 'error' : 'warning'}
                      />
                    </Box>
                    {device.revisionInfo.assignedTechnicianId && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Técnico Asignado
                        </Typography>
                        <Typography variant="body1">
                          {(() => {
                            const tech = device.revisionInfo.assignedTechnicianId;
                            if (typeof tech === 'object' && tech.firstName && tech.lastName) {
                              return `${tech.firstName} ${tech.lastName}`;
                            }
                            return (tech as any).name || tech;
                          })()}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Traccar Device Info (on-demand) */}
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Info Traccar
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={traccarLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={async () => {
                      if (!deviceId) return;
                      setTraccarLoading(true);
                      try {
                        const res = await apiService.getDeviceTraccarInfo(deviceId);
                        setTraccarInfo(res?.data || res);
                      } catch {}
                      setTraccarLoading(false);
                    }}
                    disabled={traccarLoading}
                  >
                    {traccarInfo ? 'Actualizar' : 'Cargar'}
                  </Button>
                </Stack>
                {!traccarInfo ? (
                  <Typography variant="body2" color="text.secondary">
                    Presiona "Cargar" para obtener datos en vivo de Traccar
                  </Typography>
                ) : (
                  <Box>
                    {traccarInfo.traccar && (
                      <Grid container spacing={1}>
                        {[
                          ['Traccar ID', traccarInfo.traccar.id],
                          ['Nombre', traccarInfo.traccar.name],
                          ['Unique ID', traccarInfo.traccar.uniqueId],
                          ['Estado', traccarInfo.traccar.status],
                          ['Disabled', traccarInfo.traccar.disabled ? 'Si' : 'No'],
                          ['Ultimo Update', traccarInfo.traccar.lastUpdate ? new Date(traccarInfo.traccar.lastUpdate).toLocaleString('es-DO') : 'No disponible'],
                          ['Grupo ID', traccarInfo.traccar.groupId || 'No disponible'],
                          ['Position ID', traccarInfo.traccar.positionId || 'No disponible'],
                        ].map(([label, val]) => (
                          <Grid size={{ xs: 6 }} key={label as string}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {val != null ? String(val) : 'No disponible'}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {traccarInfo.attributes && Object.keys(traccarInfo.attributes).length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Atributos</Typography>
                        <Grid container spacing={1}>
                          {Object.entries(traccarInfo.attributes).map(([key, val]: [string, any]) => (
                            <Grid size={{ xs: 6 }} key={key}>
                              <Typography variant="caption" color="text.secondary">{key}</Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                {val != null ? String(val) : 'No disponible'}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Diagnóstico completo: Traccar + SimBase + portal, a petición */}
            {deviceId && <DiagnosticoDispositivo deviceId={deviceId} />}

            {/* Perfil de uso: dónde duerme y por dónde anda, para ir a buscarlo */}
            {deviceId && <PerfilDeUso deviceId={deviceId} />}

            {/* Diagnóstico de Eventos */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Diagnóstico de Eventos
                </Typography>

                {/* Date range controls */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                  <TextField
                    type="datetime-local"
                    size="small"
                    label="Desde"
                    value={diagFrom}
                    onChange={e => setDiagFrom(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ width: 200 }}
                  />
                  <TextField
                    type="datetime-local"
                    size="small"
                    label="Hasta"
                    value={diagTo}
                    onChange={e => setDiagTo(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ width: 200 }}
                  />
                  <Button size="small" variant="contained" onClick={loadDiagnostics} disabled={diagLoading}
                    startIcon={diagLoading ? <CircularProgress size={16} /> : <RefreshIcon />}>
                    Cargar
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  {[
                    { label: '24h', hours: 24 },
                    { label: '3 días', hours: 72 },
                    { label: '7 días', hours: 168 },
                    { label: '30 días', hours: 720 },
                  ].map(p => (
                    <Button key={p.label} size="small" variant="outlined" onClick={() => setDiagPreset(p.hours)}>
                      {p.label}
                    </Button>
                  ))}
                </Stack>

                {diagError && <Alert severity="error" sx={{ mb: 2 }}>{diagError}</Alert>}

                {diagMeta && !diagLoading && (
                  <>
                    {/* Summary info */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {diagMeta.totalEvents} eventos de {diagMeta.totalPositions.toLocaleString()} posiciones
                    </Typography>

                    {/* Category summary chips */}
                    {Object.keys(diagCounts).length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Chip
                          label={`Todos (${diagEvents.length})`}
                          size="small"
                          variant={!diagCategoryFilter ? 'filled' : 'outlined'}
                          color="primary"
                          onClick={() => { setDiagCategoryFilter(null); setDiagPage(0); }}
                        />
                        {Object.entries(diagCounts)
                          .sort(([,a], [,b]) => (b as number) - (a as number))
                          .map(([cat, count]) => (
                            <Chip
                              key={cat}
                              label={`${CATEGORY_LABELS[cat] || cat} (${count})`}
                              size="small"
                              variant={diagCategoryFilter === cat ? 'filled' : 'outlined'}
                              color={diagCategoryFilter === cat ? 'primary' : 'default'}
                              onClick={() => { setDiagCategoryFilter(diagCategoryFilter === cat ? null : cat); setDiagPage(0); }}
                            />
                          ))}
                      </Stack>
                    )}

                    {/* Export button */}
                    {diagEvents.length > 0 && (
                      <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportDiagCSV} sx={{ mb: 2 }}>
                        Exportar CSV
                      </Button>
                    )}

                    {/* Events table */}
                    {filteredDiagEvents.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sin eventos {diagCategoryFilter ? `de tipo "${CATEGORY_LABELS[diagCategoryFilter] || diagCategoryFilter}"` : ''} en el rango seleccionado
                      </Typography>
                    ) : (
                      <>
                        <TableContainer sx={{ maxHeight: 500 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Hora</TableCell>
                                <TableCell>Evento</TableCell>
                                <TableCell>Código</TableCell>
                                <TableCell>Ubicación</TableCell>
                                <TableCell align="right">Vel.</TableCell>
                                <TableCell align="right">Bat.</TableCell>
                                <TableCell align="right">Power</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredDiagEvents
                                .slice(diagPage * diagRowsPerPage, diagPage * diagRowsPerPage + diagRowsPerPage)
                                .map((ev: any, idx: number) => {
                                  const globalIdx = diagPage * diagRowsPerPage + idx;
                                  const info = getEventInfo(ev.code);
                                  const speedKmh = ((ev.speed || 0) * 1.852).toFixed(0);
                                  const isExpanded = diagExpandedRow === globalIdx;
                                  return (
                                    <React.Fragment key={globalIdx}>
                                      <TableRow
                                        hover
                                        sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                                        onClick={() => setDiagExpandedRow(isExpanded ? null : globalIdx)}
                                      >
                                        <TableCell padding="checkbox">
                                          <IconButton size="small">
                                            {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                                          </IconButton>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                          {new Date(ev.time).toLocaleString('es-DO', { hour12: false })}
                                        </TableCell>
                                        <TableCell>
                                          <Chip label={info.label} size="small" color={info.color} variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ev.code}</TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="caption"
                                            sx={{ fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
                                            onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps?q=${ev.lat},${ev.lon}`, '_blank'); }}
                                          >
                                            {ev.lat?.toFixed(4)}, {ev.lon?.toFixed(4)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                          {speedKmh}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                          {ev.attributes?.battery != null ? `${Number(ev.attributes.battery).toFixed(1)}V` : '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                          {ev.attributes?.power != null ? `${Number(ev.attributes.power).toFixed(1)}V` : '-'}
                                        </TableCell>
                                      </TableRow>
                                      {isExpanded && (
                                        <TableRow>
                                          <TableCell colSpan={8} sx={{ py: 1, px: 2, bgcolor: 'action.hover' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Datos completos de la posición</Typography>
                                            <Grid container spacing={1}>
                                              {[
                                                ['Categoría', `${CATEGORY_LABELS[info.category] || info.category}`],
                                                ['Nombre interno', info.name],
                                                ['GPS válido', ev.valid ? 'Sí' : 'No'],
                                                ['Curso', ev.course != null ? `${ev.course}°` : '-'],
                                                ['Altitud', ev.altitude != null ? `${ev.altitude}m` : '-'],
                                                ['Server Time', ev.serverTime ? new Date(ev.serverTime).toLocaleString('es-DO', { hour12: false }) : '-'],
                                              ].map(([label, val]) => (
                                                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={label as string}>
                                                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{val}</Typography>
                                                </Grid>
                                              ))}
                                            </Grid>
                                            {ev.attributes && Object.keys(ev.attributes).length > 0 && (
                                              <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                  Atributos ({Object.keys(ev.attributes).length})
                                                </Typography>
                                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                  {Object.entries(ev.attributes)
                                                    .sort(([a], [b]) => a.localeCompare(b))
                                                    .map(([key, val]: [string, any]) => (
                                                      <Grid size={{ xs: 6, sm: 4, md: 3 }} key={key}>
                                                        <Typography variant="caption" color="text.secondary">{key}</Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                                                          {val != null ? String(val) : '-'}
                                                        </Typography>
                                                      </Grid>
                                                    ))}
                                                </Grid>
                                              </Box>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={filteredDiagEvents.length}
                          page={diagPage}
                          onPageChange={(_, p) => setDiagPage(p)}
                          rowsPerPage={diagRowsPerPage}
                          onRowsPerPageChange={e => { setDiagRowsPerPage(parseInt(e.target.value)); setDiagPage(0); }}
                          rowsPerPageOptions={[25, 50, 100]}
                          labelRowsPerPage="Filas:"
                        />
                      </>
                    )}
                  </>
                )}

                {!diagMeta && !diagLoading && (
                  <Typography variant="body2" color="text.secondary">
                    Selecciona un rango de fecha y presiona "Cargar" para ver los eventos del dispositivo
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Position History (on-demand) */}
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Historial de Posiciones (24h)
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={posLoading ? <CircularProgress size={16} /> : <HistoryIcon />}
                    onClick={async () => {
                      if (!deviceId) return;
                      setPosLoading(true);
                      try {
                        const res = await apiService.getDevicePositions(deviceId);
                        const data = res?.data || res;
                        setPosHistory(data.positions || []);
                      } catch {}
                      setPosLoading(false);
                    }}
                    disabled={posLoading}
                  >
                    {posHistory.length > 0 ? 'Actualizar' : 'Cargar'}
                  </Button>
                </Stack>
                {posHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Presiona "Cargar" para ver las posiciones de las ultimas 24 horas
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>{posHistory.length} posiciones encontradas</Typography>
                    {posHistory.slice(-20).reverse().map((pos: any, i: number) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.5 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {pos.latitude?.toFixed(5)}, {pos.longitude?.toFixed(5)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(pos.speed * 1.852).toFixed(0)} km/h
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(pos.deviceTime || pos.fixTime).toLocaleString('es-DO')}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {/* Quick Actions */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Acciones Rápidas
                </Typography>
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/devices/${deviceId}/edit`)}
                  >
                    Editar Dispositivo
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MaintenanceIcon />}
                    onClick={() => navigate(`/devices/${deviceId}/create-revision`)}
                    disabled={device.revisionInfo?.isInRevision}
                  >
                    Crear Revisión
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate(`/devices/${deviceId}/revision-history`)}
                  >
                    Ver Historial
                  </Button>
                  {device.simCardId && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<SimCardIcon />}
                      onClick={() => {
                        const simId = typeof device.simCardId === 'object' ? device.simCardId._id : device.simCardId;
                        navigate(`/simcards/${simId}`);
                      }}
                    >
                      Ver SIM Card
                    </Button>
                  )}
                  {device.lastPosition && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<LocationIcon />}
                      onClick={() => setMapOpen(true)}
                    >
                      Ver en Mapa
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Device Stats */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Información del Sistema
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Registro
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(device.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Última Actualización
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(device.updatedAt)}
                    </Typography>
                  </Box>
                  {device.groupType && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Grupo
                      </Typography>
                      <Chip
                        label={device.groupType}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Map Popup */}
      {device.lastPosition && (
        <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Ubicacion - {device.name || device.imei}
            <Typography variant="body2" color="text.secondary">
              {formatTimeAgo(device.lastPosition.timestamp || device.lastPosition.deviceTime)}
              {device.lastPosition.speed !== undefined && ` | ${(device.lastPosition.speed * 1.852).toFixed(0)} km/h`}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: 450 }}>
            <iframe
              title="Device Location"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${device.lastPosition.longitude - 0.005},${device.lastPosition.latitude - 0.005},${device.lastPosition.longitude + 0.005},${device.lastPosition.latitude + 0.005}&layer=mapnik&marker=${device.lastPosition.latitude},${device.lastPosition.longitude}`}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => window.open(`https://www.google.com/maps?q=${device.lastPosition.latitude},${device.lastPosition.longitude}`, '_blank')}
            >
              Abrir en Google Maps
            </Button>
            <Button onClick={() => setMapOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default DeviceDetailPage;